import {
  CloudAccountApiReadScanningConfigRequest,
  OnboardingConfigResponse,
} from '@ariksa/cloud-account/api';
import {
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  ResourceApiGetZonesByRegionForAccountRequest,
} from '@ariksa/inventory-core/api';
import flagsmith from '@flagsmith/flagsmith';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import {
  DefaultApiAddEnabledRegionsRequest,
  DefaultApiGetCrossAccountsForScanningRequest,
  DefaultApiGetEnabledRegionsRequest,
  DefaultApiReplaceEnabledRegionsRequest,
  DefaultApiUpdateScannerSettingsRequest,
  ItemApiUpdateCentralScanningAccountStatusRequest,
  OrgEnabledRegionsResponse,
} from 'services/Vulnerability/legacyRequestTypes';

import {
  getScanVaultCrossAccounts,
  getScanVaultEnabledRegions,
  mutateScanVaultEnabledRegions,
  updateScanVaultCentralScanningAccountStatus,
  updateScanVaultScannerSettings,
  ScanVaultCentralAccount,
  ScanVaultCrossAccountsResponse,
} from 'api/scan-vault/client';
import { CloudAccountService, InventoryService } from 'api/services';
import { LegacyScanAnalysis } from 'api/services/legacyScanAnalysis';
import {
  doGetCloudAccountDataScannerInfo,
  doGetCloudAccountVulnerabilityScanner,
} from 'containers/Setup/CloudAccounts/saga';
import { doGetCentralVulnerabilityScanners } from 'containers/SharedState/saga';

import { actions } from './slice';

export function* outpostSaga() {
  yield takeLatestAction(
    actions.getVulnerabilityScanners,
    doGetCloudAccountVulnerabilityScanner,
  );
  yield takeLatestAction(
    actions.getDataScanners,
    doGetCloudAccountDataScannerInfo,
  );
  yield takeLatestAction(
    actions.getAriksaOutposts,
    doGetCentralVulnerabilityScanners,
  );
  yield takeLatestAction(actions.generateOutpostToken, doGenerateOutpostToken);
  yield takeLatestAction(actions.getZones, doGetZones);
  yield takeLatestAction(actions.rescanResources, doRescanResources);
  yield takeLatestAction(actions.getAccountsScanned, doGetAccountsScanned);
  yield takeLatestAction(actions.getNetworks, doGetNetworks);
  yield takeLatestAction(actions.getSubnets, doGetSubnets);
  yield takeLatestAction(actions.getScanningConfig, doGetScannerConfig);
  yield takeLatestAction(actions.detachAccount, doDetachAccount);
  yield takeLatestAction(
    actions.enableRegionForScanning,
    doEnableRegionForScanning,
  );
  yield takeLatestAction(
    actions.getEnabledRegionsForScanning,
    doGetEnabledRegionsForScanning,
  );
  yield takeLatestAction(
    actions.updateEnabledRegionsForScanning,
    doUpdateEnabledRegionsForScanning,
  );
}

export function* doDetachAccount(
  ctx: QueryContext<
    ScanVaultCentralAccount,
    ItemApiUpdateCentralScanningAccountStatusRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () =>
        updateScanVaultCentralScanningAccountStatus({
          accountId: ctx.params.accountId,
          enabled:
            ctx.params.centralScanningAccountStatusUpdate?.status === 'Active',
        }),
      { map: value => value, errorMsg: 'Failed to detach account!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Item.updateCentralScanningAccountStatus(ctx.params),
    {
      errorMsg: 'Failed to detach account!',
    },
  );
}

export function* doGetScannerConfig(
  ctx: QueryContext<
    OnboardingConfigResponse,
    CloudAccountApiReadScanningConfigRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => CloudAccountService.CloudAccount.readScanningConfig(ctx.params),
    {
      errorMsg: 'Failed to get outpost config!',
    },
  );
}

export function* doGetEnabledRegionsForScanning(
  ctx: QueryContext<
    OrgEnabledRegionsResponse,
    DefaultApiGetEnabledRegionsRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultEnabledRegions({
          provider: ctx.params.provider,
          accountUuid: ctx.params.accountId ?? undefined,
        }).then(response => ({
          provider: response.provider,
          account_id: response.account_uuid ?? ctx.params.accountId,
          regions: response.regions,
        })),
      { map: value => value, errorMsg: 'Failed to get enabled regions!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getEnabledRegions(ctx.params),
    {
      errorMsg: 'Failed to get enabled regions!',
    },
  );
}

export function* doEnableRegionForScanning(
  ctx: QueryContext<
    OrgEnabledRegionsResponse,
    DefaultApiAddEnabledRegionsRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    const request = ctx.params.orgEnabledRegionsRequest;
    yield call(
      ctx.fetch as any,
      () =>
        mutateScanVaultEnabledRegions('POST', {
          provider: request.provider,
          accountUuid: request.account_id ?? undefined,
          regions: request.regions ?? [],
        }).then(response => ({
          provider: response.provider,
          account_id: response.account_uuid ?? request.account_id,
          regions: response.regions,
        })),
      {
        map: value => value,
        errorMsg: 'Failed to enable regions for scanning!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.addEnabledRegions(ctx.params),
    {
      errorMsg: 'Failed to enable regions for scanning!',
    },
  );
}

export function* doUpdateEnabledRegionsForScanning(
  ctx: QueryContext<
    OrgEnabledRegionsResponse,
    DefaultApiReplaceEnabledRegionsRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    const request = ctx.params.orgEnabledRegionsRequest;
    yield call(
      ctx.fetch as any,
      () =>
        mutateScanVaultEnabledRegions('PUT', {
          provider: request.provider,
          accountUuid: request.account_id ?? undefined,
          regions: request.regions ?? [],
        }).then(response => ({
          provider: response.provider,
          account_id: response.account_uuid ?? request.account_id,
          regions: response.regions,
        })),
      { map: value => value, errorMsg: 'Failed to update enabled regions!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.replaceEnabledRegions(ctx.params),
    {
      errorMsg: 'Failed to update enabled regions!',
    },
  );
}

function* doGenerateOutpostToken(ctx: QueryContext<any>) {
  //yield call(ctx.fetch, () => );
}

export function* doGetZones(
  ctx: QueryContext<string[], ResourceApiGetZonesByRegionForAccountRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Resource.getZonesByRegionForAccount(ctx.params),
    {
      errorMsg: 'Failed to get zones!',
    },
  );
}

export function* doRescanResources(
  ctx: QueryContext<any, DefaultApiUpdateScannerSettingsRequest>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () => updateScanVaultScannerSettings(ctx.params),
      { map: value => value, errorMsg: 'Failed to rescan resources!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.updateScannerSettings(ctx.params),
    {
      errorMsg: 'Failed to rescan resources!',
    },
  );
}

export function* doGetAccountsScanned(
  ctx: QueryContext<
    ScanVaultCrossAccountsResponse,
    DefaultApiGetCrossAccountsForScanningRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () => getScanVaultCrossAccounts(ctx.params.accountUuid),
      { map: value => value, errorMsg: 'Failed to get accounts scanned!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getCrossAccountsForScanning(ctx.params),
    {
      errorMsg: 'Failed to get accounts scanned!',
    },
  );
}

export function* doGetSubnets(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2(ctx.params),
    {
      errorMsg: 'Failed to get subnets!',
    },
  );
}

export function* doGetNetworks(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2(ctx.params),
    {
      errorMsg: 'Failed to get networks!',
    },
  );
}
